import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { guardianToUser } from '@/hooks/useGuardian';
import type {
  Guardian,
  User,
  UserStatus,
  Coordinator,
  AuthResponse,
  ParticipantLoginInput,
  ParticipantAuthResponse,
  ParticipantRegisterInput,
  ParticipantRegisterResponse,
  Participant,
  ParticipantEstadoRegisto,
  ParticipantSelfUpdateInput,
  ResetPasswordInput,
  MessageResponse,
  UserUpdateInput,
} from 'shared';

const participantStatusToUserStatus: Record<ParticipantEstadoRegisto, UserStatus> = {
  Pendente: 'pending',
  Ativo: 'active',
  Inativo: 'inactive',
  Suspenso: 'inactive',
};

// PWA primarily serves participants; coordinators reuse the staff endpoints.
const PARTICIPANT_BASE = '/api/auth/participant';

// Staff /api/auth/me response shape; mirrors the Web app.
interface StaffMeResponse {
  user: User;
  coordinator: Coordinator | null;
}

/**
 * Adapt a Participant (id: number, nome, apelido, optional email) into the
 * User shape the PWA UI currently consumes (id: number, name, email). This
 * keeps HomePage/ProfilePage working without a deep refactor.
 */
function participantToUser(p: Participant): User {
  return {
    id: p.id,
    email: p.email ?? '',
    name: `${p.nome} ${p.apelido}`.trim(),
    role: 'user',
    status: participantStatusToUserStatus[p.estadoRegisto],
    themePreference: 'light',
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  };
}

// Login mutation
export function useLogin() {
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation({
    mutationFn: async (data: ParticipantLoginInput): Promise<AuthResponse> => {
      const response = await api.post<ParticipantAuthResponse>(
        `${PARTICIPANT_BASE}/login`,
        data,
        { skipAuth: true }
      );
      return {
        user: participantToUser(response.user),
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
      };
    },
    onSuccess: (response) => {
      setAuth(response, 'participant');
    },
  });
}

// RegisterResult tags the two outcomes of self-registration. The API now
// creates every account as Pendente ('pending'): the caller shows the
// awaiting-approval dialog, no session is created, and calling setAuth on this
// branch is forbidden. 'active' (signed in immediately) is no longer returned
// but is still handled.
export type RegisterResult =
  | { status: 'active'; auth: AuthResponse }
  | { status: 'pending'; participantId: number };

// Register mutation — a 'pending' result leaves the RegisterPage to handle
// the UI (ID dialog + redirect to /login).
export function useRegister() {
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation({
    mutationFn: async (data: ParticipantRegisterInput): Promise<RegisterResult> => {
      const response = await api.post<ParticipantRegisterResponse>(
        `${PARTICIPANT_BASE}/register`,
        data,
        { skipAuth: true }
      );
      if (response.status === 'pending') {
        return { status: 'pending', participantId: response.pending.participantId };
      }
      return {
        status: 'active',
        auth: {
          user: participantToUser(response.active.user),
          accessToken: response.active.accessToken,
          refreshToken: response.active.refreshToken,
          expiresIn: response.active.expiresIn,
        },
      };
    },
    onSuccess: (result) => {
      if (result.status === 'active') {
        setAuth(result.auth, 'participant');
      }
    },
  });
}

// Logout mutation
export function useLogout() {
  const { refreshToken, kind, logout } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      const endpoint = kind ? `/api/auth/${kind}/logout` : '/api/auth/logout';
      return api.post(endpoint, { refreshToken });
    },
    onSettled: () => {
      logout();
      queryClient.clear();
    },
  });
}

// Get current user — /api/auth/me is kind-dispatched on the backend.
// Participants get a Participant object; staff get { user, coordinator }.
// We normalize to a single User for the rest of the PWA UI.
export function useCurrentUser() {
  const { isAuthenticated, kind } = useAuthStore();

  return useQuery({
    queryKey: ['currentUser', kind],
    queryFn: async (): Promise<User> => {
      if (kind === 'participant') {
        const p = await api.get<Participant>('/api/auth/me');
        return participantToUser(p);
      }
      if (kind === 'guardian') {
        return guardianToUser(await api.get<Guardian>('/api/auth/me'));
      }
      const staff = await api.get<StaffMeResponse>('/api/auth/me');
      return staff.user;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Get the full Participant record for the self-service edit form. Unlike
// useCurrentUser (which normalizes to User and drops the detail fields), this
// preserves every editable field. Participant-only.
export function useCurrentParticipant() {
  const { isAuthenticated, kind } = useAuthStore();

  return useQuery({
    queryKey: ['currentParticipant'],
    queryFn: () => api.get<Participant>('/api/auth/me'),
    enabled: isAuthenticated && kind === 'participant',
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Update the current participant's own profile (PUT /api/auth/me). On success
// refresh the participant cache and the normalized currentUser so the Profile
// header (name/email) reflects the change immediately, and keep the persisted
// auth store in sync via setUser.
export function useUpdateMe() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (data: ParticipantSelfUpdateInput) =>
      api.put<Participant>('/api/auth/me', data),
    onSuccess: (participant) => {
      queryClient.setQueryData(['currentParticipant'], participant);
      setUser(participantToUser(participant));
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

/** Which kind of PWA account a password reset is for. */
export type ResetAccount = 'participant' | 'guardian';

const resetBase = (account: ResetAccount) =>
  account === 'guardian' ? '/api/auth/guardian' : PARTICIPANT_BASE;

// Reset password mutation
export function useResetPassword(account: ResetAccount = 'participant') {
  return useMutation({
    mutationFn: (data: ResetPasswordInput) =>
      api.post<MessageResponse>(`${resetBase(account)}/reset-password`, data, { skipAuth: true }),
  });
}

// Update current user (staff-only endpoint; retained for API parity).
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { user, setUser } = useAuthStore();

  return useMutation({
    mutationFn: (data: UserUpdateInput) =>
      api.put<User>(`/api/users/${user?.id}`, data),
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      queryClient.setQueryData(['currentUser'], updatedUser);
    },
  });
}
